"use client";

import React, { useState, useEffect } from "react";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { emailAgentService } from "../../email-services/agentService";

interface EmailChatSettingsProps {
  agentId: string;
  initialMaxChatLength: number;
  initialTone: string;
}

const toneOptions = [
  "professional",
  "friendly",
  "casual",
  "formal",
  "enthusiastic",
  "empathetic",
  "concise",
  "detailed",
  "humorous",
  "serious",
];

export const EmailChatSettings: React.FC<EmailChatSettingsProps> = React.memo(
  ({ agentId, initialMaxChatLength, initialTone }) => {
    const [maxChatLength, setMaxChatLength] = useState(initialMaxChatLength);
    const [tone, setTone] = useState(initialTone);
    const toast = useToast();

    useEffect(() => {
      const saveSettings = async () => {
        try {
          await emailAgentService.update(agentId, {
            max_chat_length: maxChatLength,
            tone,
          });
          toast({
            title: "Settings saved",
            status: "success",
            duration: 1500,
            isClosable: true,
          });
        } catch (error: any) {
          console.error("Error saving chat settings:", error);
          toast({
            title: "Error saving settings",
            description: error?.message || "An unexpected error occurred",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };

      if (maxChatLength !== initialMaxChatLength || tone !== initialTone) {
        saveSettings();
      }
    }, [
      maxChatLength,
      tone,
      agentId,
      initialMaxChatLength,
      initialTone,
      toast,
    ]);

    return (
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            bg="transparent"
            _hover={{ bg: "orange.50" }}
            _active={{ bg: "orange.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
            }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="orange.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 2C5.58172 2 2 5.58172 2 10C2 11.8919 2.6842 13.6314 3.82338 15L3 18L6 17.1766C7.36863 18.3158 9.10814 19 11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3H10Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              Chat Settings
            </Box>
            <AccordionIcon color="orange.600" />
          </AccordionButton>
        </h2>

        <AccordionPanel pb={4} bg="gray.100">
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.800">
                Max Email Thread Length: {maxChatLength}
              </Text>

              <Slider
                min={20}
                max={200}
                step={10}
                value={maxChatLength}
                onChange={setMaxChatLength}
                colorScheme="orange"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>

              <Text fontSize="xs" color="gray.600" mt={1}>
                Maximum number of messages in an email thread
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.800">
                Tone
              </Text>

              <Menu>
                <MenuButton
                  as={Button}
                  variant="outline"
                  size="sm"
                  minW="150px"
                  rightIcon={<ChevronDownIcon />}
                  colorScheme="orange"
                  color="orange.600"
                  borderColor="orange.300"
                  bg="white"
                  _hover={{ bg: "orange.50", borderColor: "orange.400" }}
                  _active={{ bg: "orange.100", borderColor: "orange.500" }}
                  _focusVisible={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                  }}
                  textAlign="left"
                >
                  {tone.charAt(0).toUpperCase() + tone.slice(1)}
                </MenuButton>

                <MenuList
                  maxH="220px"
                  overflowY="auto"
                  borderColor="orange.200"
                  _focusWithin={{
                    borderColor: "orange.300",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)",
                  }}
                  sx={{
                    scrollbarWidth: "thin",
                    scrollbarColor:
                      "var(--chakra-colors-orange-400) transparent",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-orange-400)",
                      borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "var(--chakra-colors-orange-500)",
                    },
                  }}
                >
                  {toneOptions.map((option) => (
                    <MenuItem
                      key={option}
                      onClick={() => setTone(option)}
                      _hover={{ bg: "orange.50" }}
                      _focus={{ bg: "orange.50" }}
                      _active={{ bg: "orange.100" }}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              <Text fontSize="xs" color="gray.600" mt={1}>
                The conversational tone of your agent
              </Text>
            </Box>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    );
  }
);
